import pickBy from 'lodash.pickby';
import mapKeys from 'lodash.mapkeys';


export default class AbstractAdapter {

  /**
   * @param fieldsParams = {
   *   channels: { // filed key
   *     alias: 'channel_id', // alias (with this key item is stored)
   *     converters: {
   *       in: doSmth, // convert data from storage format
   *       out: doSmthOut // convert data to storage format
   *     }
   *   },
   *   startDate: {
   *    ....
   *   }
   *  }
   */
  constructor (fieldsParams) {
    this.fieldParams = fieldsParams;
    this.allowedFieldsList = new Set(Object.keys(this.fieldParams));
    this.allowedAliasesList = new Set(Array.from(this.allowedFieldsList).map(i => this.getKeyAlias(i)));
    this.store = {};

    this.allowedChoices = {};
    this.choicesGetters = {};
    mapKeys(this.fieldParams, (value, key) => {
      if (value.choices) {
        this.allowedChoices[key] = value.choices;
      }
      if (value.choicesGetter) {
        this.choicesGetters[key] = value.choicesGetter;
      }
    });
  }

  /**
   * Load storage-object from storage (LocalStorage, get params in url, ...)
   */
  loadStore () {
    throw Error('Not implemented');
  }

  /**
   * Save storage-object in storage (LocalStorage, get params in url, ...)
   */
  saveStore () {
    throw Error('Not implemented');
  }

  /**
   * Do processing on added field
   * @param key
   * @param value
   * returns processed value
   */
  dumpValue (key, value) {
    const params = this.fieldParams[key];
    const converter = params.converters && params.converters.out ? params.converters.out : (i) => i;
    return converter(value);
  }

  /**
   * Do processing of loaded filed
   * @param key - key of current value in storage
   * @param value - raw item value
   * returns processed value
   */
  loadValue (key, value) {
    const params = this.fieldParams[key];
    const converter = params.converters && params.converters.in ? params.converters.in : (i) => i;
    return converter(value);
  }

  /**
   * Get alias of field that is used in storage
   * @param key
   * @returns alias
   */
  getKeyAlias (key) {
    if (this.fieldParams[key].alias) {
      return this.fieldParams[key].alias;
    }
    return key;
  }

  /**
   * Save field in storage-object
   * @param key
   * @param value
   */
  applyField (key, value) {
    const aliasKey = this.getKeyAlias(key);
    this.store[aliasKey] = this.dumpValue(key, value);
  }

  /**
   * Validation function
   * @param key
   * @param value
   * @returns validated value (provided value if it is valid or default value)
   */
  validateValue (key, value) {
    let choices = null;
    if (this.allowedChoices[key]) {
      choices = this.allowedChoices[key];
    } else if (this.choicesGetters[key]) {
      choices = this.choicesGetters[key](this.store);
    }

    if (choices) {
      if (Array.isArray(value)) {

        // if values is array -> return only valid values or empty array
        const valid = value.filter(i => choices.includes(i));
        if (valid.length === 0) {
          return [];
        }
        return valid;
      } else if (!choices.includes(value)) {
        return choices[0];
      }
    }

    return value;
  }

  /**
   * Load field from storage-object
   * @param key
   * @param loadTo
   */
  loadField (key, loadTo) {
    const aliasKey = this.getKeyAlias(key);
    const valueFromStore = this.store[aliasKey];

    if (!valueFromStore) {
      return;
    }
    loadTo[key] = this.validateValue(key, this.loadValue(key, valueFromStore));
  }

  /**
   * Save fields
   * This function should be called to update store
   * @param fields
   */
  apply (fields) {
    this.store = {};
    const filteredFields = pickBy(fields, (val, key) => this.allowedFieldsList.has(key));

    Object.keys(filteredFields).forEach(i => this.applyField(i, filteredFields[i]));

    this.saveStore();
  }

  /**
   * Check that store is empty
   * @returns {boolean}
   */
  isEmpty () {
    this.loadStore();
    return Object.keys(this.store).length === 0;
  }

  /**
   * Load data from store
   * Use this function to load store
   * @param defaultFields - Default values that will be returned if they are not present in storage
   * @returns Object - processed storage object
   */
  load (defaultFields = {}) {
    this.loadStore();
    const loadedFields = {};

    this.allowedFieldsList.forEach(i => this.loadField(i, loadedFields));

    return {...defaultFields, ...loadedFields};
  }
}
